import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { IntakeFormData } from "./IntakeFormSchema";

interface AccessibilitySectionProps {
  form: UseFormReturn<IntakeFormData>;
}

const MOBILITY_AIDS = [
  { id: "wheelchair", label: "Wheelchair" },
  { id: "walker", label: "Walker" },
  { id: "cane", label: "Cane" },
  { id: "scooter", label: "Mobility Scooter" },
  { id: "crutches", label: "Crutches" },
  { id: "prosthetic", label: "Prosthetic Limb" },
];

const HEARING_DEVICES = [
  { id: "hearing_aid", label: "Hearing Aid" },
  { id: "cochlear_implant", label: "Cochlear Implant" },
  { id: "amplifier", label: "Personal Amplifier" },
  { id: "captioning", label: "Captioning Device" },
];

const VISION_DEVICES = [
  { id: "glasses", label: "Glasses/Contacts" },
  { id: "magnifier", label: "Magnifying Device" },
  { id: "screen_reader", label: "Screen Reader" },
  { id: "braille", label: "Braille Display" },
  { id: "large_print", label: "Large Print Materials" },
];

const COMMUNICATION_DEVICES = [
  { id: "aac_device", label: "AAC Device" },
  { id: "speech_app", label: "Speech Generating App" },
  { id: "text_to_speech", label: "Text-to-Speech Device" },
  { id: "communication_board", label: "Communication Board" },
];

export function AccessibilitySection({ form }: AccessibilitySectionProps) {
  const toggleArrayValue = (
    fieldName: "mobility_aids" | "hearing_devices" | "vision_devices" | "communication_devices",
    value: string,
    checked: boolean
  ) => {
    const currentValues = form.getValues(fieldName) || [];
    if (checked) {
      form.setValue(fieldName, [...currentValues, value]);
    } else {
      form.setValue(fieldName, currentValues.filter((v) => v !== value));
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-2">
        <h3 className="text-lg font-semibold text-foreground">Devices & Accessibility Tools</h3>
        <p className="text-sm text-muted-foreground">
          Select any assistive devices or accessibility tools you currently use
        </p>
      </div>

      {/* Mobility Aids */}
      <FormField
        control={form.control}
        name="mobility_aids"
        render={() => (
          <FormItem>
            <FormLabel>Mobility Aids</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {MOBILITY_AIDS.map((item) => (
                <FormField
                  key={item.id}
                  control={form.control}
                  name="mobility_aids"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(item.id)}
                          onCheckedChange={(checked) =>
                            toggleArrayValue("mobility_aids", item.id, checked as boolean)
                          }
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        {item.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Hearing Devices */}
      <FormField
        control={form.control}
        name="hearing_devices"
        render={() => (
          <FormItem>
            <FormLabel>Hearing Devices</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {HEARING_DEVICES.map((item) => (
                <FormField
                  key={item.id}
                  control={form.control}
                  name="hearing_devices"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(item.id)}
                          onCheckedChange={(checked) =>
                            toggleArrayValue("hearing_devices", item.id, checked as boolean)
                          }
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        {item.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Vision Devices */}
      <FormField
        control={form.control}
        name="vision_devices"
        render={() => (
          <FormItem>
            <FormLabel>Vision Devices</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {VISION_DEVICES.map((item) => (
                <FormField
                  key={item.id}
                  control={form.control}
                  name="vision_devices"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(item.id)}
                          onCheckedChange={(checked) =>
                            toggleArrayValue("vision_devices", item.id, checked as boolean)
                          }
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        {item.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Communication Devices */}
      <FormField
        control={form.control}
        name="communication_devices"
        render={() => (
          <FormItem>
            <FormLabel>Communication Devices</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {COMMUNICATION_DEVICES.map((item) => (
                <FormField
                  key={item.id}
                  control={form.control}
                  name="communication_devices"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(item.id)}
                          onCheckedChange={(checked) =>
                            toggleArrayValue("communication_devices", item.id, checked as boolean)
                          }
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        {item.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Other Assistive Devices */}
      <FormField
        control={form.control}
        name="other_assistive_devices"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Other Assistive Devices</FormLabel>
            <FormControl>
              <Textarea
                placeholder="List any other devices or tools not mentioned above..."
                className="min-h-[80px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Accessibility Needs */}
      <FormField
        control={form.control}
        name="accessibility_needs"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Accessibility Needs</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Describe any additional accessibility accommodations or support you may need during appointments..."
                className="min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormDescription>
              This helps us prepare appropriate accommodations for your visits
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
